import { Injectable } from '@angular/core';

import { CompetitionTreeConfig, Sitecore, SportsConfig, TimeFilterConfig } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { CountItem, ItemType, LeagueItem, VirtualCompetitionPrefix, children, takeTop } from '@frontend/sports/common/core/data-access/sport-model';
import { isDefined, xor } from '@frontend/sports/common/core/utils/extended-types';
import { CompetitionRoute } from '@frontend/sports/navigation/core/feature/models';
import { UrlParam } from '@frontend/sports/navigation/core/feature/url-builder';
import { SportUrlParam, UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { Tournament } from '@frontend/sports/types/components/tournaments';
import { DeviceService } from '@frontend/vanilla/core';
import { original, produce } from 'immer';
import { flatten, isArray, sortBy } from 'lodash-es';

import { CalendarService } from '../calendar/calendar.service';
import { IntervalFilter } from '../calendar/count-interval.service';
import { TreeItem, TreeSeparatorItem } from '../common/item/item.model';
import { TabBarItem } from '../tab-bar/tab-bar.models';
import {
    CalendarTreeItem,
    LeagueTreeItem,
    RegionTreeItem,
    SportTreeItem,
    SportTreeState,
    TournamentTreeItem,
    TreeOptions,
    VirtualGroupTreeItem,
    getItemTypeForSport,
} from './competition-list.models';

interface GroupedCountItem {
    top: CountItem[];
    all: CountItem[];
}

@Injectable({ providedIn: 'root' })
export class CompetitionTreeService {
    constructor(
        private urlHelper: UrlHelperService,
        private sitecore: Sitecore,
        private calendar: CalendarService,
        private sportConfig: SportsConfig,
        private device: DeviceService,
        private timeFilterConfig: TimeFilterConfig,
        private competitionTreeConfig: CompetitionTreeConfig,
    ) {}

    get(sport: CountItem, context?: string, eSportsLobby = false): SportTreeItem {
        const { top, all } = this.getGroupped(sport);
        const interval = this.calendar.getInterval(context);
        const options = this.getOptions({
            sport: sport.id,
            interval,
            regions: all.length,
            topRegions: top.length,
            isEsport: sport.isEsport,
            eSportsLobby,
            seoOptimize: this.device.isRobot,
        });

        const tree = new SportTreeItem({ ...sport, children: all }, options);

        if (top.length) {
            if (sport.id !== SportConstant.Tennis) {
                tree.children.unshift(new TreeSeparatorItem(this.sitecore.event.AZcountries));
            }

            tree.children.unshift(...top.map((region) => new RegionTreeItem(sport, region, options)));
        }

        return tree;
    }

    getTopCompetitions(
        sport: CountItem,
        tournaments: Tournament[] = [],
        context?: string,
        optionsParams?: Partial<TreeOptions>,
    ): SportTreeItem | undefined {
        const interval = this.calendar.getInterval(context);
        if (interval && !this.timeFilterConfig.isTimeFilterEnabled) {
            return;
        }

        const options = {
            ...this.getOptions({ sport: sport.id, interval, isEsport: sport.isEsport }),
            ...optionsParams,
        };

        options.priceBoostCountMapper = this.getPriceBoostCount();

        if (!options.topTitle) {
            throw new Error('Top title should be defined');
        }

        const topLeaguesOrder = this.getTopLeaguesOrder();

        let regions;

        if (sport.id === SportConstant.Tennis) {
            regions = children(sport, ItemType.Tournament);
        } else {
            regions = children(sport, ItemType.Region);
        }

        const allLeagues = children(sport, ItemType.Competition);
        const virtualLeagues = allLeagues.filter((league) => league.isVirtual);
        const nonVirtualLeagues = allLeagues.filter((league) => !league.isVirtual);
        const groups = children(sport, ItemType.VirtualCompetitionGroup);

        const leagueIdToRegionMap = this.getChildMap(regions);
        const groupIdToLeagueMap = this.getChildMap(allLeagues);

        const visibleLeaguesOrder = new Map<LeagueItem, number>();

        // Adding VirtualCompetitionGroups
        const visibleGroups: LeagueItem[] = [];
        for (const league of groups) {
            const siblingsWithOrder = this.getSiblingsWithOrder(league.siblings, topLeaguesOrder);
            if (siblingsWithOrder.length === 0) {
                continue;
            }

            visibleLeaguesOrder.set(league, Math.min(...siblingsWithOrder.map((siblingWithOrder) => siblingWithOrder.order)));
            siblingsWithOrder.forEach((sibling) => topLeaguesOrder.delete(sibling.siblingId));
            visibleGroups.push(league);
        }

        // Adding VirtualCompetitions
        const visibleLeagues: LeagueItem[] = [];
        for (const league of virtualLeagues) {
            const siblingsWithOrder = this.getSiblingsWithOrder(league.siblings, topLeaguesOrder);
            if (siblingsWithOrder.length === 0) {
                continue;
            }

            visibleLeaguesOrder.set(league, Math.min(...siblingsWithOrder.map((siblingWithOrder) => siblingWithOrder.order)));
            siblingsWithOrder.forEach((sibling) => topLeaguesOrder.delete(sibling.siblingId));
            visibleLeagues.push(league);
        }

        // Adding 'normal' Leagues
        for (const league of nonVirtualLeagues) {
            const siblingsWithOrder = this.getSiblingsWithOrder(league.siblings, topLeaguesOrder);
            if (siblingsWithOrder.length === 0) {
                continue;
            }

            visibleLeaguesOrder.set(league, Math.min(...siblingsWithOrder.map((siblingWithOrder) => siblingWithOrder.order)));
            siblingsWithOrder.forEach((sibling) => topLeaguesOrder.delete(sibling.siblingId));
            visibleLeagues.push(league);
        }

        const leagueAndGroupsSortedList = visibleLeagues
            .concat(visibleGroups)
            .sort((first, second) => visibleLeaguesOrder.get(first)! - visibleLeaguesOrder.get(second)!);

        if (tournaments.length + visibleLeagues.length + visibleGroups.length === 0) {
            return;
        }

        const leagueAndGroupsTreeItems = leagueAndGroupsSortedList.map((item) => {
            if (item.type === ItemType.VirtualCompetitionGroup) {
                const groupLeague = groupIdToLeagueMap.get(item.id)!;
                const groupRegion = leagueIdToRegionMap.get(groupLeague.id)!;

                return new VirtualGroupTreeItem(sport, groupRegion, groupLeague, item, options);
            }

            const region = leagueIdToRegionMap.get(item.id)!;

            return new LeagueTreeItem(sport, region, item, options);
        });

        const tournamentTreeItems = tournaments.map(
            (tournament) => new TournamentTreeItem(sport, tournament, this.urlHelper.getTournamentUrl.bind(this.urlHelper)),
        );

        const tree = new SportTreeItem({ ...sport, children: [] }, options);
        tree.title = options.topTitle;
        tree.subTitle = options.subTitle;
        tree.expanded = true;
        tree.active = true;
        tree.children = tournamentTreeItems.concat(leagueAndGroupsTreeItems);
        tree.priceBoostCount = options.priceBoostCountMapper ? options.priceBoostCountMapper(sport) : 0;

        return tree;
    }

    getTopRegions(sport: CountItem, context?: string): SportTreeItem {
        const setChildren = (item: CountItem, items: CountItem[] = []) => ({
            ...item,
            children: items,
        });
        const { top, all } = this.getGroupped(sport);
        const interval = this.calendar.getInterval(context);
        const options = this.getOptions({ sport: sport.id, interval, regions: all.length, topRegions: top.length, isEsport: sport.isEsport });
        const tree = new SportTreeItem(
            setChildren(
                sport,
                top.map((item) => setChildren(item)),
            ),
            options,
        );
        if (sport.id === SportConstant.Tennis) {
            tree.moreTitle = options.title;
        }

        return tree;
    }

    getAll(sport: CountItem, state?: SportTreeState): TreeItem | undefined {
        if (!state?.context) {
            return;
        }

        const interval = this.calendar.getInterval(state.context);

        if (!interval || (interval && this.timeFilterConfig.isTimeFilterEnabled)) {
            return;
        }

        const item = new CalendarTreeItem(sport, interval, (current, name) => this.urlHelper.getSportUrl(current, name));

        item.active = !state.region && !state.league;

        return item;
    }

    setState(sport?: TreeItem, state?: SportTreeState): TreeItem | undefined {
        if (sport instanceof SportTreeItem) {
            // top tree has TournamentTreeItem and CompetitionTreeItem children
            if (sport.children[0] instanceof RegionTreeItem) {
                return this.setTreeState(sport, state);
            }

            return this.setTopTreeState(sport, state);
        }

        return;
    }

    toggleExpanded(tree: TreeItem, item: TreeItem): TreeItem {
        return produce(tree, (draft) => {
            this.traverse(original(draft)!, draft, (originalItem, draftItem) => {
                if (originalItem === item) {
                    draftItem.expanded = !originalItem.expanded;

                    return true;
                }

                return false;
            });
        });
    }

    private traverse(root: TreeItem, draft: TreeItem, action: (originalItem: TreeItem, draftItem: TreeItem) => boolean): boolean {
        if (action(root, draft)) {
            return true;
        }

        for (const child of root.children) {
            const draftChild = draft.children[root.children.indexOf(child)];
            if (this.traverse(child, draftChild, action)) {
                return true;
            }
        }

        return false;
    }

    private getSiblingsWithOrder(siblingIds: number[], sortOrderMap: Map<number, number>): { siblingId: number; order: number }[] {
        const siblingsWithOrder: { siblingId: number; order: number }[] = [];
        siblingIds.forEach((siblingId) => {
            const order = sortOrderMap.get(siblingId);
            if (order !== undefined) {
                siblingsWithOrder.push({ siblingId, order });
            }
        });

        return siblingsWithOrder;
    }

    private setTreeState(sport: SportTreeItem, state?: SportTreeState): SportTreeItem {
        if (!state?.sport) {
            return sport;
        }

        return produce(sport, (draft) => {
            this.setDeselected(draft);
            this.setSelected(draft);
            this.setExpanded(draft, !!state.sportExpanded);

            const selectedRegionId = state.region || (draft.children.length === 1 ? draft.children[0].id : undefined);

            // as we have the top regions, the same region might appear twice
            draft.children
                .filter((region) => region.id === selectedRegionId)
                .forEach((region, index) => {
                    this.setSelected(region, index === 0);

                    if (state.league) {
                        const leagues = isArray(state.league) ? state.league : [state.league];
                        region.children.filter((league) => leagues.some((y) => y === league.id)).forEach((league) => this.setSelected(league));
                    } else {
                        this.setSelected(region.children[0]);
                    }
                });

            if (!state.regionExpanded) {
                return;
            }

            draft.children.filter((region) => state.regionExpanded!.indexOf(region.id) !== -1).forEach((region) => this.setExpanded(region));
        });
    }

    private setTopTreeState(sport: SportTreeItem, state?: SportTreeState): SportTreeItem {
        if (!state) {
            return sport;
        }

        return produce(sport, (draft) => {
            const leagues = isArray(state.league) ? state.league : [state.league];

            this.setExpanded(draft, !!state.sportExpanded);

            if (state.isVirtual) {
                draft.children.forEach((child) => {
                    child.active = false;
                });

                const virtualGroup = draft.children.find(
                    (child) => child.id === state.virtualCompetitionGroup && child instanceof VirtualGroupTreeItem,
                );

                if (virtualGroup) {
                    virtualGroup.active = true;

                    return;
                }

                const virtualCompetition = draft.children.find(
                    (child) => child.id === state.league && child instanceof LeagueTreeItem && child.isVirtual,
                );

                if (virtualCompetition) {
                    virtualCompetition.active = true;
                }

                return;
            }

            draft.children.forEach((child) => (child.active = leagues.some((league) => league === child.id) || child.id === state.tournament));
        });
    }

    private setExpanded(item: TreeItem, expanded: boolean = true): void {
        item.expanded = this.device.isRobot || expanded;
    }

    private setSelected(item: TreeItem, expanded: boolean = true): void {
        item.active = true;
        this.setExpanded(item, expanded);
    }

    private setDeselected(item: TreeItem): void {
        if (item.active) {
            item.active = false;
            item.children.forEach((child) => this.setDeselected(child));
        }
    }

    private getTopLeaguesOrder(): Map<number, number> {
        const source = this.sportConfig.topCompetitions.map((item, index) => [item, index]);

        return new Map(source as [number, number][]);
    }

    private getChildMap<T extends CountItem>(source: T[]): Map<number, T> {
        const sourceChildren = source.map((item) => item.children.map<[number, T]>((child) => [child.id, item]));

        return new Map(flatten(sourceChildren));
    }

    getGroupped(sport: CountItem): GroupedCountItem {
        return sport.id === SportConstant.Tennis ? this.getGroupedTournaments(sport) : this.getGroupedRegions(sport);
    }

    private getGroupedRegions(sport: CountItem): GroupedCountItem {
        const regions = children(sport, ItemType.Region);
        const top = takeTop(regions);
        const all = regions.sort((a, b) => a.name.localeCompare(b.name));

        return { top, all };
    }

    private getGroupedTournaments(sport: CountItem): GroupedCountItem {
        const tournaments = children(sport, ItemType.Tournament);
        const isTop = (tournament: CountItem) => isDefined(tournament.order) || tournament.children.some((league) => isDefined(league.order));
        const filterTop = (topTournaments: boolean) => tournaments.filter((tournament) => xor(!topTournaments, isTop(tournament)));

        const top = sortBy(filterTop(true), (tournament) => tournament.order);
        const all = sortBy(filterTop(false), (tournament) => tournament.name);

        return { top, all };
    }

    private getOptions(settings: {
        sport: number;
        interval?: IntervalFilter;
        regions?: number;
        topRegions?: number;
        isEsport?: boolean;
        eSportsLobby?: boolean;
        seoOptimize?: boolean;
    }): TreeOptions {
        const options: TreeOptions = {
            urlMapper: this.getUrl(settings.interval, settings.eSportsLobby),
            countMapper: this.competitionTreeConfig.showCountDsl ? this.getCount(settings.interval) : (item) => 0,
            title: this.sitecore.event.AllCompetitionsTitle,
            subTitle: this.sitecore.event.AZCompetitions,
            topTitle: this.sitecore.event.TopTournaments,
            moreTitle: this.sitecore.event.AllTournaments,
            allTitle: this.sitecore.globalMessages.All,
            seoOptimize: settings.seoOptimize,
            eSportsLobby: settings.eSportsLobby,
        };

        if (settings.sport !== SportConstant.Tennis) {
            options.title = this.sitecore.event.AllCompetitionsTitle;
            options.topTitle = this.sitecore.event.TopCompetitions;
            options.moreTitle = this.sitecore.event.AllCompetitionsTitle;

            if (!settings.topRegions && settings.regions && settings.regions > 1) {
                options.title = this.sitecore.event.AZCountriesTitle;
                options.moreTitle = this.sitecore.event.AllCountriesFooterText;
            }

            if (settings.topRegions || (settings.regions && settings.regions > 1)) {
                options.title = this.sitecore.event.AllCountriesTitle;
                options.subTitle = this.sitecore.event.AZcountries;
                options.moreTitle = this.sitecore.event.AllCountriesFooterText;
            }
        }

        if (settings.isEsport) {
            options.title = this.sitecore.eSports.CompetitionsByRegion;
        }

        if (settings.interval && !this.timeFilterConfig.isTimeFilterEnabled) {
            options.title = `${settings.interval.title} - ${options.title}`;
        }

        return options;
    }

    private getUrl(
        interval?: IntervalFilter,
        eSportsLobby?: boolean,
    ): (sport?: UrlParam, region?: UrlParam, league?: UrlParam, group?: UrlParam) => string {
        return (sport, region, league, group) => {
            let context: string | undefined = interval ? interval.name : SportUrlParam.Betting;
            if (eSportsLobby && sport && !region && !league && !group) {
                context = undefined;
            }

            return this.urlHelper.getSportUrl(sport, context, region, league, group);
        };
    }

    private getCount(interval?: IntervalFilter): (item?: CountItem) => number {
        return (item) => {
            if (!item?.counts) {
                return 0;
            }

            if (interval) {
                return item.counts.preMatch + item.counts.live;
            }

            return item.counts.preMatch + item.counts.live + item.counts.specials;
        };
    }

    private getPriceBoostCount(): (item?: CountItem) => number {
        return (item) => item?.counts?.priceBoost ?? 0;
    }

    getCalendarPills(sport: CountItem | undefined, route: CompetitionRoute): TabBarItem[] | undefined {
        if (sport) {
            const tree = this.getTopCompetitions(sport, [], route.context);

            return this.getCompetitionsTabs(route, sport, tree);
        }

        return undefined;
    }

    getCompetitionsTabs(route: CompetitionRoute, sport: CountItem | undefined, topcompetitions?: SportTreeItem): TabBarItem[] {
        const pills: TabBarItem[] = [];
        const leaguePrefix = route.isVirtual ? VirtualCompetitionPrefix : undefined;

        const selectedTab = route.league ? route.league : Number(route.region) || 0;

        topcompetitions?.children.map((competition) =>
            pills.push({
                active: competition.id === selectedTab,
                icon: this.getRegionId(sport, Number(competition.id)),
                id: Number(competition.id) || 0,
                title: competition.title,
                url: competition?.href,
                ignoreBrowserHistory: true,
            }),
        );

        if ((route.league || route.region) && !pills.some((p) => p.id === selectedTab)) {
            pills.unshift({
                active: route.region !== undefined || route.league !== undefined,
                icon: `country-icon c${route.region}`,
                id: route.league ? Number(route.league) : Number(route.region) || 0,
                title: route.league ? this.getLeague(sport, Number(route.league)) : this.getRegion(sport, route.region),
                url: this.urlHelper.getSportUrl(
                    sport,
                    route.context,
                    this.getUrlParameter(route.region, route.regionName),
                    this.getUrlParameter(route.league, route.leagueName, leaguePrefix),
                ),
                ignoreBrowserHistory: true,
            });
        }

        pills.unshift({
            active: route.region === undefined && route.league === undefined,
            icon: `country-icon c-1`,
            id: -1,
            title: sport!.id !== SportConstant.Tennis ? this.sitecore.event.AllCountriesTitle : this.sitecore.event.AllCompetitionsTitle,
            url: this.urlHelper.getSportUrl(sport, route.context),
            ignoreBrowserHistory: true,
        });

        if (pills.length === 1) {
            pills[0].active = true;
        }

        return pills.filter((pill) => pill.title);
    }

    getRegionId(sport: CountItem | undefined, leaugeid: number): string {
        let regionid;
        if (sport) {
            const regions = children(sport, ItemType.Region);
            const leagueIdToRegionMap = this.getChildMap(regions);
            regionid = leagueIdToRegionMap.get(leaugeid)?.id;
        }

        return `country-icon c${regionid}`;
    }

    private getRegion(sport: CountItem | undefined, id?: number): string {
        let region: UrlParam | undefined;
        if (id && sport) {
            const type = getItemTypeForSport(sport.id);

            region = this.getChildren(children(sport, type), id);
        }

        return region ? region.name : '';
    }

    private getLeague(sport: CountItem | undefined, id?: number): string {
        let league: UrlParam | undefined;
        if (id && sport) {
            league = this.getChildren(children(sport, ItemType.Competition), id);
        }

        return league ? league.name : '';
    }

    private getChildren(source: UrlParam[], id: number): UrlParam | undefined {
        return source.filter((item) => item.id === id).pop();
    }

    getUrlParameter(id?: number[] | number, name?: string, prefix?: string): UrlParam | undefined {
        if (!id || !name || Array.isArray(id)) {
            return;
        }

        const urlParameter: UrlParam = { id, name };
        if (prefix) {
            urlParameter.prefix = prefix;
        }

        return urlParameter;
    }
}
